import { Modal, Timeline, Tag, Typography, Tooltip, Skeleton, Empty } from 'antd';
import moment from 'moment';
import 'moment/locale/pt-br';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import * as actions from './actions';

var Text = Typography.Text;

var TimelineContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
  padding-right: 8px;
`;

var TimelineItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

var TimelineHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

var ChangesList = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #f9f9f9;
  border-radius: 4px;
  font-size: 12px;
`;

var ChangeItem = styled.div`
  color: #666;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

var ACTION_CONFIG = {
    created: { color: 'green', text: 'Criado' },
    updated: { color: 'blue', text: 'Atualizado' },
    status_changed: { color: 'orange', text: 'Status Alterado' },
    deleted: { color: 'red', text: 'Excluído' }
};

var FIELD_LABELS = {
    amount: 'Mensalidade',
    paid: 'Valor Pago',
    status: 'Status',
    method: 'Forma PGTO',
    paid_at: 'Data Pagamento',
    interest: 'Juros',
    discount: 'Desconto',
    justification: 'Justificativa',
    additionals: 'Adicionais',
    additionals_amount: 'Valor Adicionais',
    amount13: '13º Salário',
    paid13: '13º Pago'
};

var PAYMENT_METHODS = {
    1: 'Dinheiro',
    2: 'Boleto',
    3: 'Cartão',
    4: 'Transferência',
    5: 'Pix',
    6: 'Depósito',
    7: 'Cheque'
};

function formatValue(field, value) {
    if (value === null || value === undefined) return '-';

    if (field === 'method') {
        return PAYMENT_METHODS[value] || value;
    }
    if (field === 'paid_at') {
        return moment(value).format('DD/MM/YYYY');
    }
    if (['amount', 'paid', 'interest', 'discount', 'additionals_amount', 'amount13', 'paid13'].indexOf(field) !== -1) {
        return 'R$ ' + Number(value).toFixed(2).replace('.', ',');
    }
    if (field === 'additionals' && Array.isArray(value)) {
        return value.length + ' item(s)';
    }
    return String(value);
}

function renderChanges(changes) {
    if (!changes) return null;

    var before = changes.before || {};
    var after = changes.after || {};
    var fields = Object.keys(after);

    if (fields.length === 0) {
        fields = Object.keys(before);
    }

    if (fields.length === 0) return null;

    return (
        <ChangesList>
            {fields.map(function(field) {
                var label = FIELD_LABELS[field] || field;
                var oldValue = formatValue(field, before[field]);
                var newValue = formatValue(field, after[field]);

                return (
                    <ChangeItem key={field}>
                        <strong>{label}:</strong> {oldValue} → {newValue}
                    </ChangeItem>
                );
            })}
        </ChangesList>
    );
}

function HistoryModal(props) {
    var isOpen = props.isOpen;
    var closeModal = props.closeModal;
    var honoraryId = props.honoraryId;

    var dispatch = useDispatch();
    var _useState = useState(false);
    var isLoading = _useState[0];
    var setIsLoading = _useState[1];
    var _useState2 = useState([]);
    var logs = _useState2[0];
    var setLogs = _useState2[1];

    useEffect(function() {
        if (isOpen && honoraryId) {
            setIsLoading(true);
            loadLogs();
        }
    }, [isOpen, honoraryId]);

    function loadLogs() {
        dispatch(actions.loadLogs(honoraryId))
            .then(function(response) {
                var data = response && response.payload && response.payload.data && response.payload.data.data;
                setLogs(data || []);
            })
            .catch(function(error) {
                console.error('Error loading logs:', error);
                setLogs([]);
            })
            .finally(function() {
                setIsLoading(false);
            });
    }

    function formatTime(datetime) {
        moment.locale('pt-br');
        return moment(datetime).fromNow();
    }

    function formatFullDate(datetime) {
        moment.locale('pt-br');
        return moment(datetime).format('DD/MM/YYYY [às] HH:mm:ss');
    }

    function handleClose() {
        setLogs([]);
        closeModal();
    }

    return (
        <Modal
            title="Histórico de Alterações"
            visible={isOpen}
            onCancel={handleClose}
            footer={null}
            width={600}
        >
            {isLoading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
            ) : logs.length === 0 ? (
                <Empty description="Nenhum histórico encontrado" />
            ) : (
                <TimelineContainer>
                    <Timeline>
                        {logs.map(function(log) {
                            var config = ACTION_CONFIG[log.action] || { color: 'default', text: log.action };
                            var userName = log.user ? log.user.name : 'Sistema';

                            return (
                                <Timeline.Item key={log.id} color={config.color}>
                                    <TimelineItemContent>
                                        <TimelineHeader>
                                            <Tooltip title={formatFullDate(log.created_at)}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {formatTime(log.created_at)}
                                                </Text>
                                            </Tooltip>
                                            <Tag color={config.color}>
                                                {config.text}
                                            </Tag>
                                            <Text strong>
                                                {userName}
                                            </Text>
                                        </TimelineHeader>
                                        {log.description && (
                                            <div style={{ color: '#333', fontSize: 13 }}>
                                                {log.description}
                                            </div>
                                        )}
                                        {renderChanges(log.changes)}
                                    </TimelineItemContent>
                                </Timeline.Item>
                            );
                        })}
                    </Timeline>
                </TimelineContainer>
            )}
        </Modal>
    );
}

export default HistoryModal;
